import PropTypes from 'prop-types';
import React, { Component } from "react";
import Relay from "react-relay"
import Modal from "react-modal"
import { debounce } from "lodash/function"
import MapBox from "../elements/MapBox"
import { getMapBox } from "../../scripts/mapBox"
import Icon from "../elements/Icon"
import ArticleResult from "./ArticleResult"
import { Set } from "immutable"
import { getStyles, getStyleTag } from '../../styles/dynamicStyles'

const REGEX_REMOVE_REACT_IDS = /\s?data-reactid="[^"]+"/g
const MAXIMUM_ZOOM = 16
const MAP_MOVE_DEBOUNCE_DELAY = 1500
const MAX_MODAL_RESULTS = 9

class ResultsMap extends Component {
  static propTypes = {
    bbox: PropTypes.string,
    exactLocations: PropTypes.array.isRequired,
    onMapMoved: PropTypes.func,
    expanded: PropTypes.bool
  }

  state = {
    popupId: null,
    showingModal: false,
    modalIds: Set(),
    searchBoundingBox: null,
    showMoreResults: false
  }

  popupRef = null

  withPopupRef = () =>
    new Promise(resolve => {
      const map = this.popupRef
      if (map) resolve(map)
      else {
        setTimeout(() => resolve(this.withPopupRef()), 200)
      }
    })

  onMapLoaded = map => {
    const mapboxgl = getMapBox()
    const { onMapMoved } = this.props
    this.map = map
    this.fitBounds(map)
    this.addClusters(map)
    map.addControl(new mapboxgl.NavigationControl(), "bottom-right")
    setTimeout(() => {
      map.on(
        "moveend",
        debounce(() => {
          if (onMapMoved) {
            const bounds = map.getBounds()
            const NW = bounds.getNorthWest()
            const SE = bounds.getSouthEast()
            return onMapMoved({
              top_left: [NW.lng, NW.lat],
              bottom_right: [SE.lng, SE.lat]
            })
          }
        }, MAP_MOVE_DEBOUNCE_DELAY)
      )
    }, 2000)
  }

  componentDidUpdate(prevProps) {
    if (this.map && this.props.exactLocations) {
      this.updateClusters(this.map)
    }
    if (prevProps.expanded !== this.props.expanded && this.map) {
      setTimeout(() => {
        this.map.resize()
      }, 500)
    }
  }

  componentWillUnmount() {
    if (this.map) {
      this.map.remove()
    }
  }

  fitBounds = map => {
    const mapboxgl = getMapBox()
    const points = this.props.exactLocations
      .map(({ node }) => node.coordinates)
      .filter(c => Array.isArray(c) && c.length === 2)

    if (points.length) {
      const bounds = points.reduce(
        (bounds, p) => bounds.extend(p),
        new mapboxgl.LngLatBounds(points[0], points[0])
      )
      map.fitBounds(bounds)
    }
  }

  getFeatureCollection = () => {
    const { exactLocations } = this.props
    if (!exactLocations) return
    const features = exactLocations.map(({ node }) => node).map(node => {
      const resourceIds = node.resources.edges.map(
        ({ node: resourceNode }) => resourceNode.id
      )
      return {
        type: "Feature",
        properties: {
          point_count: node.count > 1 ? node.count : undefined,
          allIds: resourceIds,
          id: resourceIds[0],
          boundingBox: node.bounding_box
        }, 
        geometry: {
          type: "Point",
          coordinates: node.coordinates
        }
      }
    })
    return {
      type: "FeatureCollection",
      features
    }
  }

  updateClusters = debounce(map => {
    if (map) {
      const source = map.getSource("results")
      if (source) {
        const data = this.getFeatureCollection()
        source.setData(data)
      }
    }
  }, 400)

  addClusters = map => {
    const data = this.getFeatureCollection()
    map.addSource("results", {
      type: "geojson",
      cluster: false,
      data
    })

    map.addLayer({
      id: "clusters",
      type: "circle",
      source: "results",
      filter: ["has", "point_count"],
      paint: {
        // Use step expressions (https://www.mapbox.com/mapbox-gl-js/style-spec/#expressions-step)
        // with three steps to implement three types of circles:
        //   * 20px circles when point count is less than 100
        //   * 30px circles when point count is between 100 and 750
        //   * 40px circles when point count is greater than or equal to 750

        "circle-color": "#A31B41",
        "circle-radius": ["step", ["get", "point_count"], 20, 100, 30, 750, 40],
        "circle-stroke-width": 2,
        "circle-stroke-color": "#fff"
      }
    })

    map.addLayer({
      id: "cluster-count",
      type: "symbol",
      source: "results",
      filter: ["has", "point_count"],
      layout: {
        "text-field": "{point_count}",
        // "text-font": ["CalibreWeb", "Calibre", "Helvetica", "Arial", "sans-serif"], // strange... our preferred fontstack doesn't work... low priority so leaving for now
        "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
        "text-size": 14
      },
      paint: {
        "text-color": "#ffffff"
      }
    })

    map.addLayer({
      id: "unclustered-point",
      type: "circle",
      source: "results",
      filter: ["!has", "point_count"],
      paint: {
        "circle-color": "#A31B41",
        "circle-radius": 10,
        "circle-stroke-width": 2,
        "circle-stroke-color": "#fff"
      }
    })

    // When a click event occurs on a feature in the places layer, open a popup at the
    // location of the feature, with description HTML from its properties.
    map.on("click", "unclustered-point", e => {
      const coordinates = e.features[0].geometry.coordinates.slice()
      const id = e.features[0].properties.id

      // Ensure that if the map is zoomed out such that multiple
      // copies of the feature are visible, the popup appears
      // over the copy being pointed to.
      while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
        coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360
      }

      this.doPopup({ coordinates, id })
    })

    // Change the cursor to a pointer when the mouse is over the places layer.
    map.on("mouseenter", "unclustered-point", () => {
      map.getCanvas().style.cursor = "pointer"
    })

    // Change it back to a pointer when it leaves.
    map.on("mouseleave", "unclustered-point", () => {
      map.getCanvas().style.cursor = ""
    })

    // Change the cursor to a pointer when the mouse is over the places layer.
    map.on("mouseenter", "clusters", () => {
      map.getCanvas().style.cursor = "pointer"
    })

    // Change it back to a pointer when it leaves.
    map.on("mouseleave", "clusters", () => {
      map.getCanvas().style.cursor = ""
    })

    map.on("click", "clusters", e => {
      const currentZoom = map.getZoom()
      const features = map.queryRenderedFeatures(e.point, { layers: ['clusters'] });
      const clusterBBox = JSON.parse(features[0].properties.boundingBox)
      if (currentZoom >= MAXIMUM_ZOOM) {
        const allIds = Set(JSON.parse(e.features[0].properties.allIds))
        const showMoreResults = allIds.size > MAX_MODAL_RESULTS ? true : false
        this.setState({ showingModal: true, modalIds: allIds, searchBoundingBox: clusterBBox, showMoreResults: showMoreResults })
      } else {
        map.fitBounds([clusterBBox[0], clusterBBox[1]], { padding: 100 })
      }
    })
  }
 
  getPopupResource = resultParams => {
    const { popupId } = this.state
    if (!popupId) return null
    return resultParams.reduce((resource, params) => {
      if (resource) {
        return resource
      }
      if (params.resource.id === popupId) {
        return params.resource
      }
    }, null)
  }

  getClusterResources = () => {
    const { modalIds, showingModal } = this.state
    const { exactLocations } = this.props
    return showingModal && modalIds.size
      ? exactLocations.reduce(
          (found, { node }) =>
            node.resources.edges.reduce((foundInEdge, { node: resource }) => {
              if (modalIds.has(resource.id)) {
                return foundInEdge.concat(resource)
              }
              return foundInEdge
            }, found),
          []
        )
      : []
  }

  getResultParams = () => {
    const { exactLocations } = this.props

    const exactResults = exactLocations.map(({ node }) => {
      const resource = node.resources.edges[0].node
      return {
        id: node.id,
        count: node.count,
        coordinates: node.coordinates,
        exact: true,
        allIds: node.resources.edges.map(({ node }) => node.id),
        resource
      }
    })

    return exactResults
  }

  handleCloseModal = () => {
    this.setState({ showingModal: false, modalIds: Set() })
  }

  thumbClick = (e, { count, resourceId, exact, coordinates, allIds }) => {
    if (exact) {
      e.preventDefault()
      if (count > 1 && allIds) {
        this.setState({ showingModal: true, modalIds: Set(allIds) })
      } else {
        this.doPopup({ coordinates, id: resourceId })
      }
    }
  }

  doPopup = ({ coordinates, id }) => {
    this.setState({ popupId: id }, () => {
      this.withPopupRef().then(ref => {
        const html = ref.innerHTML.replace(REGEX_REMOVE_REACT_IDS, "")
        new mapboxgl.Popup()
          .setLngLat(coordinates)
          .setHTML(html)
          .addTo(this.map)
      })
    })
  }

  showMoreResults = () => {
    const { updateSearchBoundingBox } = this.props
    const bbox = this.state.searchBoundingBox
    this.handleCloseModal()
    return updateSearchBoundingBox({
      top_left: [bbox[0][0], bbox[0][1]],
      bottom_right: [bbox[1][0], bbox[1][1]]
    }, false)
  }

  render() {
    const { showingModal } = this.state
    const results = this.getResultParams()

    const popupResource = this.getPopupResource(results)
    const modalResources = this.getClusterResources()

    return (
      <section className="search-results__listings" data-article-style="map">
        <div className="search-results-map">
          <div className="search-results-map__map">
            <MapBox
              id="search"
              zoom={10}
              onMapLoaded={this.onMapLoaded}
              mapOptions={{ scrollZoom: false }}
            />
          </div>
        </div>
        <Modal
          isOpen={showingModal}
          ariaHideApp={false}
          onRequestClose={this.handleCloseModal}
        >
          {getStyleTag('modal')}
          <span>
            <div className="modal-inner" style={getStyles('modal-inner')}>
              <section
                className="search-results__listings"
                data-article-style="grid"
              >
                {modalResources.map(resource => (
                  <ArticleResult key={resource.id} resource={resource} />
                ))}
              </section>
            </div>
            {this.state.showMoreResults ? (
              <button className="modal-show-more" 
                      style={getStyles('modal-show-more')}
                      onClick={this.showMoreResults}>
                {getStyleTag('modal-show-more')}
                Show more results
              </button>
            ) : null }
            {getStyleTag('modal-close')}
            <button className="modal-close" 
                    style={getStyles('modal-close')}
                    onClick={this.handleCloseModal}>
              <Icon title="Close modal" icon="cross" />
            </button>
          </span>
        </Modal>
        <div style={{ display: "none" }}>
          {/*
            Popup markup is rendered here, then pulled into the mapboxgl popup as html if needed around line ~200
            I'm not sure if this is clever or a nasty hack
          */}
          {popupResource && (
            <div
              ref={el => {
                this.popupRef = el
              }}
            >
              <ArticleResult resource={popupResource} />
            </div>
          )}
        </div>
      </section>
    )
  }
}

export default Relay.createContainer(ResultsMap, {
  fragments: {
    exactLocations: () => Relay.QL`
      fragment on ExactLocationEdge @relay(plural: true) {
        node {
          count
          coordinates
          bounding_box
          resources(first:1000) {
            edges {
              node {
                id
                ${ArticleResult.getFragment("resource")}
              }
            }
          }
        }
      }
    `
  }
})



// WEBPACK FOOTER //
// ./webapp/components/modules/ResultsMap.js